class HeroVideo extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    this.deferredMedia = this.querySelector('hero-video-deferred-media');
    this.playButton = this.querySelector('.deferred-media__poster');
    
    if (this.playButton && this.deferredMedia) {
      this.playButton.addEventListener('click', this.handlePlay.bind(this));
    }

    // Handle autoplay settings - check after a delay to allow for content loading
    setTimeout(() => {
      this.videoElement = this.querySelector('video, iframe');
      this.handleAutoplay();
      this.handleVideoBehavior();
      this.handleResponsiveVideo();
    }, 200);
  }

  handlePlay() {
    if (this.deferredMedia && this.deferredMedia.loadContent) {
      this.deferredMedia.loadContent();
    }
  }

  handleAutoplay() {
    const autoplayEnabled = this.dataset.autoplay === 'true';
    const mutedEnabled = this.dataset.muted === 'true';
    
    if (autoplayEnabled && this.videoElement && this.videoElement.tagName === 'VIDEO') {
      this.videoElement.autoplay = true;
      
      // Ensure video is muted for autoplay to work in most browsers
      if (mutedEnabled) {
        this.videoElement.muted = true;
      }
      
      // Try to play the video
      this.videoElement.play().catch(error => {
        console.log('Video autoplay failed:', error);
      });
    }
  }

  handleVideoBehavior() {
    const behavior = this.dataset.behavior;
    const mediaElement = this.querySelector('.hero-video__media');
    
    if (!mediaElement || !behavior) return;
    
    switch (behavior) {
      case 'fixed':
        this.handleFixedBehavior(mediaElement);
        break;
      case 'ambient':
        this.handleAmbientBehavior(mediaElement);
        break;
      case 'zoom-in':
        this.handleZoomBehavior(mediaElement);
        break;
    }
  }

  handleFixedBehavior(mediaElement) {
    // Fixed background video effect
    mediaElement.style.position = 'fixed';
    mediaElement.style.top = '0';
    mediaElement.style.left = '0';
    mediaElement.style.width = '100vw';
    mediaElement.style.height = '100vh';
    mediaElement.style.zIndex = '-1';
  }

  handleAmbientBehavior(mediaElement) {
    // Subtle floating animation
    mediaElement.style.animation = 'ambient 20s ease-in-out infinite';
  }

  handleZoomBehavior(mediaElement) {
    // Zoom in/out effect
    if (this.videoElement) {
      this.videoElement.style.animation = 'zoom-in 20s ease-in-out infinite';
    }
  }

  handleResponsiveVideo() {
    if (!this.videoElement) return;
    
    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        this.adjustVideoSize(entry.contentRect);
      }
    });
    
    resizeObserver.observe(this);
  }

  adjustVideoSize(rect) {
    if (!this.videoElement || this.videoElement.tagName !== 'VIDEO') return;
    
    const video = this.videoElement;
    const containerAspectRatio = rect.width / rect.height;
    const videoAspectRatio = video.videoWidth / video.videoHeight;
    
    if (videoAspectRatio > containerAspectRatio) {
      // Video is wider than container
      video.style.width = 'auto';
      video.style.height = '100%';
    } else {
      // Video is taller than container
      video.style.width = '100%';
      video.style.height = 'auto';
    }
  }

  // Handle video loading states
  handleVideoLoad() {
    if (this.videoElement && this.videoElement.tagName === 'VIDEO') {
      this.videoElement.addEventListener('loadedmetadata', () => {
        this.adjustVideoSize(this.getBoundingClientRect());
      });
      
      this.videoElement.addEventListener('canplay', () => {
        this.videoElement.style.opacity = '1';
      });
    }
  }
}

// Register the custom element
if (!customElements.get('hero-video')) {
  customElements.define('hero-video', HeroVideo);
}

// Handle deferred media loading for hero videos
class HeroVideoDeferredMedia extends HTMLElement {
  constructor() {
    super();
    this.loadContent = this.loadContent.bind(this);
    this.poster = this.querySelector('.deferred-media__poster');
    this.template = this.querySelector('template');
    this.loaded = false;
    
    if (this.poster) {
      this.poster.addEventListener('click', this.loadContent);
    }
    
    // Auto-load if autoplay is enabled
    if (this.dataset.autoplay === 'true') {
      // Delay auto-load slightly to ensure DOM is ready
      setTimeout(() => this.loadContent(), 100);
    }
  }

  loadContent() {
    if (!this.template || this.loaded) return;
    
    window.pauseAllMedia && window.pauseAllMedia();
    
    const content = this.template.content.cloneNode(true);
    const mediaElement = content.querySelector('video, iframe');
    
    if (mediaElement) {
      this.appendChild(mediaElement);
      this.loaded = true;
      this.setAttribute('loaded', 'true');
      
      // Hide poster after loading content
      if (this.poster) {
        this.poster.style.display = 'none';
      }
      
      // Handle video attributes
      if (mediaElement.tagName === 'VIDEO') {
        this.setupVideo(mediaElement);
      } else if (mediaElement.tagName === 'IFRAME') {
        this.setupIframe(mediaElement);
      }
      
      // Focus the loaded element for accessibility
      mediaElement.focus();
    }
  }

  setupVideo(video) {
    const autoplay = this.dataset.autoplay === 'true';
    const loop = this.dataset.loop === 'true';
    const muted = this.dataset.muted === 'true';
    
    if (autoplay) video.autoplay = true;
    if (loop) video.loop = true;
    if (muted) video.muted = true;
    
    video.controls = true;
    video.playsInline = true;
    video.preload = 'metadata';
    
    // Ensure video fills container
    video.style.width = '100%';
    video.style.height = '100%';
    video.style.objectFit = 'cover';
    
    // Try to play if autoplay is enabled
    if (autoplay) {
      video.play().catch(error => {
        console.log('Video autoplay failed:', error);
        // If autoplay fails, ensure controls are visible
        video.controls = true;
      });
    }
  }

  setupIframe(iframe) {
    // Iframe attributes are set in the template
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.border = 'none';
  }
}

// Register deferred media custom element
if (!customElements.get('hero-video-deferred-media')) {
  customElements.define('hero-video-deferred-media', HeroVideoDeferredMedia);
}

// Initialize hero videos when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  const heroVideos = document.querySelectorAll('.hero-video');
  
  heroVideos.forEach(heroVideo => {
    // Optimize heading text for mobile
    optimizeHeadingForMobile(heroVideo);
    
    // Handle intersection observer for performance
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const video = entry.target.querySelector('video');
          if (video && video.paused && entry.target.dataset.autoplay === 'true') {
            video.play().catch(error => {
              console.log('Video play failed:', error);
            });
          }
        } else {
          const video = entry.target.querySelector('video');
          if (video && !video.paused) {
            video.pause();
          }
        }
      });
    }, {
      threshold: 0.5
    });
    
    observer.observe(heroVideo);
  });
});

// Function to optimize heading size based on content length and mobile screen
function optimizeHeadingForMobile(heroVideo) {
  const heading = heroVideo.querySelector('.hero-video__heading');
  if (!heading) return;
  
  const textLength = heading.textContent.trim().length;
  
  // Categorize heading length and set data attribute for CSS
  if (textLength <= 20) {
    heading.setAttribute('data-length', 'short');
  } else if (textLength <= 40) {
    heading.setAttribute('data-length', 'medium');
  } else {
    heading.setAttribute('data-length', 'long');
  }
  
  // Add mobile-specific optimizations
  if (window.innerWidth <= 749) {
    // Ensure proper mobile rendering
    heading.style.setProperty('font-display', 'swap');
    
    // Add better mobile accessibility
    heading.setAttribute('role', 'heading');
    heading.setAttribute('aria-level', '1');
    
    // Optimize for mobile performance
    heading.style.setProperty('will-change', 'transform');
    heading.style.setProperty('transform', 'translateZ(0)');
  }
}

// Handle window resize for responsive video and heading optimization
window.addEventListener('resize', () => {
  const heroVideos = document.querySelectorAll('hero-video');
  heroVideos.forEach(heroVideo => {
    if (heroVideo.handleResponsiveVideo) {
      heroVideo.handleResponsiveVideo();
    }
    // Re-optimize heading for new screen size
    optimizeHeadingForMobile(heroVideo);
  });
});
